/*
 * Kivora Home — cart helpers (drawer + cart page).
 *
 * 1. <kv-cart-progress> / <kv-money>: thresholds are stored in the shop currency (USD). When the shopper browses in
 *    another currency, these elements re-express the amounts with Shopify.currency.rate (server Liquid cannot convert).
 *    The free-gift step state is recomputed here too, so it can react to session flags (gift declined / not free).
 * 2. Free gift: when the qualifying subtotal (items subtotal minus the gift product's own lines) reaches the gift
 *    threshold and nothing in the cart blocks the gift, add one gift unit (tagged with the hidden line property
 *    `_kv_gift`); when the cart no longer qualifies, remove that auto-added unit again.
 *
 * Cart state comes from a fresh Liquid render of the cart-drawer section (script[data-kv-cart-config], built by
 * snippets/kv-cart-progress.liquid), because only Liquid knows product tags: whether a live Deal of the Week item or
 * 3+ Buy-2-Get-1 items are in the cart. Those offers don't stack with the "Free gift over $60" discount, so the gift is
 * only auto-added when neither can apply, and taken back out when one starts to apply.
 *
 * Dawn's cart rows post /cart/change with a line NUMBER. Adding or removing the gift line renumbers every other line,
 * so a gift mutation must never overlap with a shopper edit or leave the rows stale:
 *   - it waits until Dawn has no cart request in flight and the shopper has not touched a cart control for a moment;
 *   - it locks the cart controls (quantity, remove, add-gift) from the moment it posts until the response's sections
 *     are rendered, which happens immediately — before any verification;
 *   - it re-reads the cart if anything happened while it was waiting.
 *
 * Guard rails: one run at a time (in-flight flag + one queued re-run), max 8 cart requests per minute (each POST
 * counts), never touches a gift line the shopper added themselves, never removes a gift line with quantity > 1,
 * remembers (per session) when the shopper removed the gift, and takes the gift straight back out if Shopify did not
 * discount it to 0 or the cart total went up — a shopper is never charged for a gift they did not choose.
 */
(() => {
  if (window.KvCart) return;

  const ORIGIN = 'kv-cart';
  // Dawn's <cart-items>/<cart-drawer-items> skip events from source 'cart-items' instead of re-fetching a static
  // section render; kv-cart has already rendered every cart section from its own response.
  const PUBLISH_SOURCE = 'cart-items';
  const GIFT_PROP = '_kv_gift';
  const KEY_DECLINED = 'kv-gift-declined';
  const KEY_BLOCKED = 'kv-gift-blocked';
  const KEY_FAILED = 'kv-gift-failed';
  const MAX_MUTATIONS_PER_MIN = 8;
  const QUIET_MS = 500; // Dawn debounces quantity changes by 300 ms
  const QUIET_TIMEOUT_MS = 8000;
  const CONTROL = 'quantity-input button, .quantity__input, cart-remove-button, [data-kv-gift-add]';

  const store = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (e) {
        /* storage unavailable: behave as a fresh session */
      }
    },
    del(key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch (e) {
        /* ignore */
      }
    },
  };

  /* ---------------------------------------------------------------- currency helpers */

  const currencyInfo = () => (window.Shopify && window.Shopify.currency) || {};

  function rate() {
    const r = parseFloat(currencyInfo().rate);
    return r > 0 ? r : 1;
  }

  function activeCurrency(fallback) {
    return currencyInfo().active || fallback || 'USD';
  }

  function formatMoney(cents, currency, trim) {
    const amount = Math.max(0, cents) / 100;
    const whole = trim && Math.round(amount) === amount;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
      }).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed(whole ? 0 : 2)}`;
    }
  }

  function fillTemplate(target, template, amountText) {
    if (!target || typeof template !== 'string') return;
    target.textContent = '';
    template.split('[amount]').forEach((chunk, index) => {
      if (index > 0) {
        const strong = document.createElement('strong');
        strong.setAttribute('data-kv-amt', '');
        strong.textContent = amountText;
        target.appendChild(strong);
      }
      if (chunk) target.appendChild(document.createTextNode(chunk));
    });
  }

  class KvMoney extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-convert')) return;
      const cents = parseInt(this.dataset.cents, 10);
      if (Number.isNaN(cents)) return;
      this.textContent = formatMoney(Math.round(cents * rate()), activeCurrency(), this.hasAttribute('data-trim'));
      this.classList.add('is-ready');
    }
  }

  /* Same states as snippets/kv-cart-progress.liquid (gift_state), plus 'ready' = qualifies but the shopper removed
     the gift this session, so offer it back with a button. */
  function giftStateFor(d, gift, giftSubtotal) {
    if (gift <= 0) return 'off';
    if (d.giftApplied === 'true') return 'done';
    if (giftSubtotal < gift) return d.giftPromise === 'true' ? 'todo' : 'off';
    if (d.giftInCart === 'true') return d.giftConflict === 'true' ? 'blocked' : 'off';
    if (d.giftReachable !== 'true') return 'off';
    if (store.get(KEY_BLOCKED) || (d.cartSig && store.get(KEY_FAILED) === d.cartSig)) return 'off';
    if (store.get(KEY_DECLINED)) return 'ready';
    return 'pending';
  }

  class KvCartProgress extends HTMLElement {
    connectedCallback() {
      this.localise();
    }

    localise() {
      const d = this.dataset;
      const convert = this.classList.contains('kv-cp--convert');
      const r = rate();
      const currency = activeCurrency(d.shopCurrency);
      const ship = Math.round((parseInt(d.ship, 10) || 0) * r);
      const gift = Math.round((parseInt(d.gift, 10) || 0) * r);
      const shipSubtotal = parseInt(d.shipSubtotal, 10) || 0;
      const giftSubtotal = parseInt(d.giftSubtotal, 10) || 0;
      const shipDone = ship > 0 && shipSubtotal >= ship;
      const giftState = giftStateFor(d, gift, giftSubtotal);

      const shipMsg = this.querySelector('[data-kv-msg="ship"]');
      if (shipMsg) {
        shipMsg.classList.toggle('is-done', shipDone);
        if (convert) {
          fillTemplate(shipMsg.querySelector('.kv-cp__text--todo'), d.tShipAway, formatMoney(ship - shipSubtotal, currency));
        }
      }

      const giftMsg = this.querySelector('[data-kv-msg="gift"]');
      if (giftMsg) {
        giftMsg.dataset.state = giftState;
        giftMsg.classList.toggle('is-done', giftState === 'done');
        giftMsg.hidden = !(
          giftState === 'done' ||
          giftState === 'blocked' ||
          giftState === 'ready' ||
          (giftState === 'todo' && (ship === 0 || shipDone))
        );
        if (convert) {
          fillTemplate(
            giftMsg.querySelector('.kv-cp__text--todo'),
            d.tGiftAway,
            formatMoney(gift - giftSubtotal, currency)
          );
        }
        const add = giftMsg.querySelector('[data-kv-gift-add]');
        if (add) add.hidden = giftState !== 'ready';
      }

      const giftTrack = giftState === 'done' || giftState === 'todo' || giftState === 'pending' || giftState === 'ready';
      const max = Math.max(ship, giftTrack ? gift : 0);
      const pct = (value) => (max > 0 ? Math.max(0, Math.min(100, Math.floor((value * 100) / max))) : 0);
      const track = this.querySelector('.kv-cp__track');
      if (track) track.hidden = max === 0;
      const fill = this.querySelector('.kv-cp__fill');
      if (fill) fill.style.width = `${pct(shipSubtotal)}%`;
      const shipMark = this.querySelector('[data-kv-mark="ship"]');
      if (shipMark) {
        shipMark.style.left = `${pct(ship)}%`;
        shipMark.classList.toggle('is-done', shipDone);
      }
      const giftMark = this.querySelector('[data-kv-mark="gift"]');
      if (giftMark) {
        giftMark.hidden = !giftTrack;
        giftMark.style.left = `${pct(gift)}%`;
        giftMark.classList.toggle('is-done', giftState !== 'todo');
      }

      this.classList.toggle(
        'is-complete',
        (ship === 0 || shipDone) && giftState !== 'todo' && giftState !== 'pending'
      );
      this.classList.remove('kv-cp--convert');
    }
  }

  if (!customElements.get('kv-money')) customElements.define('kv-money', KvMoney);
  if (!customElements.get('kv-cart-progress')) customElements.define('kv-cart-progress', KvCartProgress);

  const refreshProgress = () => {
    document.querySelectorAll('kv-cart-progress').forEach((el) => {
      if (typeof el.localise === 'function') el.localise();
    });
  };

  /* ---------------------------------------------------------------- cart I/O */

  const routes = () => window.routes || {};
  const cartUrl = () => routes().cart_url || '/cart';
  const addUrl = () => routes().cart_add_url || '/cart/add';
  const changeUrl = () => routes().cart_change_url || '/cart/change';
  const updateUrl = () => routes().cart_update_url || '/cart/update';

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  function readConfig(root = document) {
    const el = root.querySelector('script[data-kv-cart-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function getCart() {
    return fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((res) => {
      if (!res.ok) throw new Error(`cart.js ${res.status}`);
      return res.json();
    });
  }

  function postOnce(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok || (data && data.status && data.status >= 400)) {
        const error = new Error((data && (data.description || data.message)) || `HTTP ${res.status}`);
        error.status = res.status;
        throw error;
      }
      return data;
    });
  }

  /* Retries once on a transient failure. `safeRetry` = the request is idempotent (removing a line by key); an add is
     only retried on 429, where Shopify certainly did not apply it. */
  async function post(url, body, safeRetry) {
    try {
      return await postOnce(url, body);
    } catch (e) {
      const status = e.status || 0;
      const transient = status === 429 || (safeRetry && (status === 0 || status >= 500));
      if (!transient || !allowMutation()) throw e;
      await wait(status === 429 ? 2000 : 1200);
      return postOnce(url, body);
    }
  }

  /* ---------------------------------------------------------------- section targets + rendering */

  function sectionTargets() {
    const targets = [];

    const drawer = document.querySelector('cart-drawer');
    if (drawer && document.getElementById('CartDrawer')) {
      targets.push({
        section: 'cart-drawer',
        apply(html, itemCount) {
          const source = parse(html).querySelector('#CartDrawer');
          const destination = document.getElementById('CartDrawer');
          if (!source || !destination) return;
          destination.innerHTML = source.innerHTML;
          const overlay = document.getElementById('CartDrawer-Overlay');
          if (overlay && typeof drawer.close === 'function') overlay.addEventListener('click', drawer.close.bind(drawer));
          if (typeof itemCount === 'number') drawer.classList.toggle('is-empty', itemCount === 0);
        },
      });
    }

    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble) {
      targets.push({
        section: 'cart-icon-bubble',
        apply(html) {
          const source = parse(html).querySelector('.shopify-section');
          if (source) bubble.innerHTML = source.innerHTML;
        },
      });
    }

    const pageItems = document.getElementById('main-cart-items');
    if (pageItems && pageItems.dataset.id) {
      targets.push({
        section: pageItems.dataset.id,
        apply(html, itemCount) {
          const source = parse(html).querySelector('.js-contents');
          const destination = pageItems.querySelector('.js-contents');
          if (source && destination) destination.innerHTML = source.innerHTML;
          const cartItems = document.querySelector('cart-items');
          if (cartItems && typeof itemCount === 'number') cartItems.classList.toggle('is-empty', itemCount === 0);
        },
      });
    }

    const pageFooter = document.getElementById('main-cart-footer');
    if (pageFooter && pageFooter.dataset.id) {
      targets.push({
        section: pageFooter.dataset.id,
        apply(html, itemCount) {
          const source = parse(html).querySelector('.js-contents');
          const destination = pageFooter.querySelector('.js-contents');
          if (source && destination) destination.innerHTML = source.innerHTML;
          if (typeof itemCount === 'number') pageFooter.classList.toggle('is-empty', itemCount === 0);
        },
      });
    }

    return targets.slice(0, 4);
  }

  /* Remember which cart control had focus (by line key, not line number — the gift shifts line numbers) so a
     re-render does not throw keyboard and screen-reader users back to the top of the drawer. */
  function snapshotFocus() {
    const el = document.activeElement;
    if (!el || el === document.body || !el.closest) return null;
    const host = el.closest('#CartDrawer, #main-cart-items, #main-cart-footer');
    if (!host) return null;
    const row = el.closest('.cart-item');
    const keyInput = row && row.querySelector('[data-quantity-line-key]');
    let selector = null;
    if (el.matches('[name="plus"], [name="minus"]')) selector = `[name="${el.getAttribute('name')}"]`;
    else if (el.matches('.quantity__input')) selector = '.quantity__input';
    else if (el.closest('cart-remove-button')) selector = 'cart-remove-button button, cart-remove-button a';
    else if (el.matches('[data-kv-gift-add]')) selector = '[data-kv-gift-add]';
    else if (el.id && window.CSS && CSS.escape) selector = `#${CSS.escape(el.id)}`;
    else if (el.classList.length && window.CSS && CSS.escape) selector = `.${CSS.escape(el.classList[0])}`;
    return {
      key: keyInput ? keyInput.dataset.quantityLineKey : null,
      selector,
      inDrawer: Boolean(el.closest('#CartDrawer')),
    };
  }

  function restoreFocus(snap) {
    const drawer = document.querySelector('cart-drawer');
    const drawerOpen = Boolean(drawer && drawer.classList.contains('active'));
    let target = null;
    if (snap && snap.selector) {
      const scope = snap.inDrawer ? document.getElementById('CartDrawer') : document;
      if (scope && snap.key) {
        const input = Array.from(scope.querySelectorAll('[data-quantity-line-key]')).find(
          (node) => node.dataset.quantityLineKey === snap.key && (snap.inDrawer || !node.closest('#CartDrawer'))
        );
        const row = input && input.closest('.cart-item');
        target = row ? row.querySelector(snap.selector) : null;
      } else if (scope) {
        target = scope.querySelector(snap.selector);
      }
      if (target && (target.hidden || target.disabled || target.offsetParent === null)) target = null;
    }

    if (drawerOpen && typeof window.trapFocus === 'function' && (!snap || snap.inDrawer)) {
      // The drawer's focus trap holds references to the old (replaced) first/last elements: rebuild it.
      const container = drawer.classList.contains('is-empty') ? drawer.querySelector('.drawer__inner-empty') : drawer;
      const fallback = drawer.querySelector('.drawer__inner') || container;
      if (container) window.trapFocus(container, target || fallback);
    } else if (target) {
      target.focus({ preventScroll: true });
    }
  }

  function render(sections, targets, itemCount) {
    if (!sections) return;
    const focus = snapshotFocus();
    targets.forEach((target) => {
      if (!sections[target.section]) return;
      try {
        target.apply(sections[target.section], itemCount);
      } catch (e) {
        console.warn('[kv-cart] render failed for', target.section, e);
      }
    });
    restoreFocus(focus);
  }

  function updateCount(cart) {
    if (!cart || typeof cart.item_count !== 'number') return;
    document.querySelectorAll('.kv-cart-count').forEach((el) => {
      el.textContent = cart.item_count;
    });
  }

  function announce(cart) {
    updateCount(cart);
    if (cart && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: PUBLISH_SOURCE, origin: ORIGIN, cartData: cart });
    }
  }

  /* ---------------------------------------------------------------- lock + quiet detection */

  const state = {
    initialised: false,
    busy: false,
    again: false,
    timer: null,
    lastAuto: false,
    lastAutoSig: null,
    locked: false,
    mutating: false,
    needsSync: false,
    lastActivity: 0,
    mutations: [],
  };

  function controlFrom(event) {
    const node = event.target;
    if (!node || !node.closest) return null;
    const control = node.closest(CONTROL);
    return control && control.closest('cart-items, cart-drawer-items') ? control : null;
  }

  // Capture phase: runs before Dawn's own listeners, so a blocked click never reaches quantity-input / cart.js.
  function guard(event) {
    const control = controlFrom(event);
    if (!control) return;
    if (state.locked) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    if (!control.matches('[data-kv-gift-add]')) state.lastActivity = Date.now();
  }

  function lock() {
    state.locked = true;
    document.querySelectorAll('cart-items, cart-drawer-items').forEach((el) => {
      el.classList.add('kv-cart-locked');
      el.setAttribute('aria-busy', 'true');
    });
  }

  function unlock() {
    state.locked = false;
    document.querySelectorAll('.kv-cart-locked').forEach((el) => {
      el.classList.remove('kv-cart-locked');
      el.removeAttribute('aria-busy');
    });
  }

  function isQuiet() {
    if (document.querySelector('.cart__items--disabled')) return false; // Dawn's cart request in flight
    if (Date.now() - state.lastActivity < QUIET_MS) return false; // a debounced quantity change may be pending
    const active = document.activeElement;
    if (
      active &&
      active.matches &&
      active.matches('.quantity__input') &&
      active.closest('cart-items, cart-drawer-items') &&
      active.value !== active.getAttribute('value')
    ) {
      return false; // the shopper is typing a quantity
    }
    return true;
  }

  async function waitForQuiet() {
    const start = Date.now();
    while (!isQuiet()) {
      if (Date.now() - start > QUIET_TIMEOUT_MS) return false;
      await wait(150);
    }
    return true;
  }

  function allowMutation() {
    const now = Date.now();
    state.mutations = state.mutations.filter((time) => now - time < 60000);
    if (state.mutations.length >= MAX_MUTATIONS_PER_MIN) {
      console.warn('[kv-cart] free gift paused for a minute: too many cart changes');
      return false;
    }
    state.mutations.push(now);
    return true;
  }

  /* Locked POST: the cart controls stay disabled until the response's sections are on screen. Call only right after
     waitForQuiet() resolved, in the same task, so no Dawn request can start in between. */
  async function mutate(url, body, targets, { safeRetry, itemCount } = {}) {
    if (!allowMutation()) return null;
    const extra = { sections: targets.map((target) => target.section), sections_url: window.location.pathname };
    lock();
    state.mutating = true;
    try {
      const response = await post(url, { ...body, ...extra }, safeRetry);
      const count = typeof response.item_count === 'number' ? response.item_count : itemCount;
      render(response.sections, targets, count);
      return response;
    } finally {
      state.mutating = false;
      unlock();
    }
  }

  /* ---------------------------------------------------------------- free gift engine */

  async function fetchState(targets) {
    const ids = ['cart-drawer'];
    targets.forEach((target) => {
      if (!ids.includes(target.section)) ids.push(target.section);
    });
    const url = `${cartUrl()}?sections=${encodeURIComponent(ids.slice(0, 5).join(','))}`;
    // No `Accept: application/json` here: on /cart that header makes Shopify answer with cart.js instead of sections.
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) throw new Error(`sections ${res.status}`);
    const sections = await res.json();
    const config = sections && sections['cart-drawer'] ? readConfig(parse(sections['cart-drawer'])) : null;
    if (!config) throw new Error('kv cart state missing');
    return { config, sections };
  }

  function analyse(config) {
    const giftLines = Array.isArray(config.giftLines) ? config.giftLines : [];
    const autoLines = giftLines.filter((line) => line.auto);
    const threshold = Math.round((config.giftThreshold || 0) * rate());
    return {
      giftLines,
      autoLines,
      signature: config.sig || '',
      reachable: Boolean(config.reachable),
      unlocked: threshold > 0 && (config.subtotal || 0) >= threshold,
    };
  }

  function plan(analysis) {
    if (analysis.autoLines.length > 1) {
      return { type: 'dedupe', keys: analysis.autoLines.slice(1).map((line) => line.key) };
    }

    const auto = analysis.autoLines[0];
    if (auto && auto.quantity === 1) {
      // Below the threshold, or a Deal of the Week / Buy 2 Get 1 offer now applies: the gift would cost money or
      // push that saving off another line.
      if (!analysis.unlocked || !analysis.reachable) return { type: 'remove', key: auto.key };
      if (auto.final > 0) return { type: 'remove', key: auto.key, failedSignature: analysis.signature, confirm: true };
      if (store.get(KEY_FAILED) === analysis.signature) return { type: 'remove', key: auto.key };
      return null;
    }

    if (analysis.unlocked && analysis.reachable && analysis.giftLines.length === 0) {
      if (store.get(KEY_DECLINED) || store.get(KEY_BLOCKED)) return null;
      if (store.get(KEY_FAILED) === analysis.signature) return null;
      return { type: 'add', signature: analysis.signature };
    }
    return null;
  }

  async function removeLine(key, targets) {
    let cart;
    try {
      cart = await mutate(`${changeUrl()}.js`, { id: key, quantity: 0 }, targets, { safeRetry: true });
    } catch (e) {
      // Shopify re-keys a line when its discounts change (e.g. Buy 2 Get 1 kicking in a beat after the shopper's
      // edit), so the key read a moment ago can be gone. Look the auto gift up again and retry once.
      if (![400, 404, 422].includes(e.status)) throw e;
      const fresh = await fetchState(targets);
      const line = analyse(fresh.config).autoLines[0];
      if (!line || line.key === key || line.quantity !== 1 || !isQuiet()) return null; // next run decides
      cart = await mutate(`${changeUrl()}.js`, { id: line.key, quantity: 0 }, targets, { safeRetry: true });
    }
    if (cart) announce(cart);
    return cart;
  }

  async function addGift(action, config, targets) {
    const before = config.total;
    let added;
    try {
      added = await mutate(
        `${addUrl()}.js`,
        { items: [{ id: config.giftVariantId, quantity: 1, properties: { [GIFT_PROP]: '1' } }] },
        targets,
        { itemCount: Math.max(1, (config.itemCount || 0) + 1) }
      );
    } catch (e) {
      // Sold out / not purchasable: stop trying this session. Anything else gets a fresh try on the next cart change.
      if (e.status === 422) store.set(KEY_BLOCKED, 'add');
      throw e;
    }
    if (!added) return false;
    state.lastAuto = true;
    state.lastAutoSig = action.signature;

    // The rows on screen already match the server. Now check that Shopify really made the gift free.
    const verifyStart = Date.now();
    let check = await fetchState(targets);
    let line = analyse(check.config).autoLines[0];
    if (line && line.final > 0) {
      await wait(900); // discounts can settle a beat after the add
      check = await fetchState(targets);
      line = analyse(check.config).autoLines[0];
    }

    const sameCart = check.config.sig === action.signature && state.lastActivity < verifyStart;
    const costsSomething = Boolean(line && line.final > 0);
    const raisesTotal = Boolean(line && sameCart && typeof before === 'number' && check.config.total > before);
    if (line && (costsSomething || raisesTotal || !check.config.reachable)) {
      // Not free for this cart (not discounted, or it pushed another saving off): take it back out.
      store.set(KEY_FAILED, action.signature);
      refreshProgress();
      console.warn('[kv-cart] free gift would not be free for this cart — removed it again.');
      if (!(await waitForQuiet())) return false; // the next run sees KEY_FAILED and removes it
      state.lastAuto = false;
      await removeLine(line.key, targets);
      return true;
    }

    // Free: show the settled prices (the add response can predate the discount), unless the shopper is mid-edit.
    if (isQuiet() && state.lastActivity < verifyStart) render(check.sections, targets, check.config.itemCount);
    const cart = await getCart().catch(() => null);
    if (cart) announce(cart);
    return true;
  }

  async function apply(action, config, targets) {
    if (action.type === 'add') return addGift(action, config, targets);

    if (action.type === 'remove') {
      if (action.failedSignature) {
        store.set(KEY_FAILED, action.failedSignature);
        refreshProgress();
      }
      state.lastAuto = false;
      return Boolean(await removeLine(action.key, targets));
    }

    if (action.type === 'dedupe') {
      const updates = {};
      action.keys.forEach((key) => {
        updates[key] = 0;
      });
      const cart = await mutate(`${updateUrl()}.js`, { updates }, targets, { safeRetry: true });
      if (cart) announce(cart);
      return Boolean(cart);
    }

    return false;
  }

  async function run() {
    const pageConfig = readConfig();
    if (!pageConfig || !pageConfig.giftEnabled || !pageConfig.giftVariantId || !pageConfig.giftProductId) return;
    if (window.Shopify && window.Shopify.designMode) return;
    if (state.busy) {
      state.again = true;
      return;
    }
    state.busy = true;
    let confirmed = false;

    try {
      for (let step = 0; step < 4; step += 1) {
        const targets = sectionTargets();
        const readAt = Date.now();
        const snap = await fetchState(targets);
        const config = snap.config;
        if (!config.giftEnabled || !config.giftVariantId) break;

        // Another script changed the cart while a gift request was in flight: its render may be older than ours.
        if (state.needsSync && isQuiet() && state.lastActivity < readAt) {
          state.needsSync = false;
          render(snap.sections, targets, config.itemCount);
        }

        const analysis = analyse(config);
        // The shopper took the auto-added gift out while still qualifying (the rest of the cart is exactly as it was
        // when the gift was last seen): respect that for this session.
        if (
          state.lastAuto &&
          state.lastAutoSig === analysis.signature &&
          analysis.autoLines.length === 0 &&
          analysis.giftLines.length === 0 &&
          analysis.unlocked &&
          analysis.reachable
        ) {
          store.set(KEY_DECLINED, '1');
          refreshProgress();
        }
        state.lastAuto = analysis.autoLines.length > 0;
        state.lastAutoSig = state.lastAuto ? analysis.signature : null;

        const action = plan(analysis);
        if (!action) break;
        if (action.confirm && !confirmed) {
          // A gift that is not free right now may be a discount still settling: look again before removing it.
          confirmed = true;
          await wait(900);
          continue;
        }

        if (!(await waitForQuiet())) break;
        if (state.lastActivity >= readAt) continue; // the cart may have changed while we waited: read it again
        if (!(await apply(action, config, targets))) break;
      }
    } catch (e) {
      console.warn('[kv-cart] free gift check failed', e);
    } finally {
      unlock();
      state.busy = false;
      document.querySelectorAll('[data-kv-gift-add][aria-busy]').forEach((button) => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      });
      if (state.again) {
        state.again = false;
        schedule();
      }
    }
  }

  function schedule(delay = 250) {
    clearTimeout(state.timer);
    state.timer = setTimeout(run, delay);
  }

  function onClick(event) {
    const button = event.target.closest && event.target.closest('[data-kv-gift-add]');
    if (!button || state.locked) return;
    event.preventDefault();
    store.del(KEY_DECLINED);
    store.del(KEY_BLOCKED);
    store.del(KEY_FAILED);
    state.mutations = [];
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    schedule(0);
  }

  function init() {
    if (state.initialised) return;
    state.initialised = true;

    const config = readConfig();
    if (config) {
      state.lastAuto = Boolean(config.autoGiftInCart);
      state.lastAutoSig = state.lastAuto ? config.sig || '' : null;
    }

    document.addEventListener('click', guard, true);
    document.addEventListener('change', guard, true);
    document.addEventListener(
      'keydown',
      (event) => {
        if (controlFrom(event) && !state.locked) state.lastActivity = Date.now();
      },
      true
    );

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && (event.origin === ORIGIN || event.source === ORIGIN)) return;
        state.lastActivity = Date.now();
        if (state.mutating) state.needsSync = true;
        if (event && event.cartData) updateCount(event.cartData);
        schedule();
      });
    }

    document.addEventListener('click', onClick);
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) schedule(0);
    });

    if (config && config.giftEnabled && config.needsCheck) schedule(0);
  }

  window.KvCart = { check: () => schedule(0), version: 3 };

  if (document.readyState === 'complete') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init, { once: true });
    window.addEventListener('load', init, { once: true });
  }
})();
